import React from "react"

import { useEvent } from "shared/lib/useEvent"
import { useMergedRefs } from "shared/lib/useMergedRefs"
import { Tooltip } from "shared/ui/tooltip/Tooltip"

import { TabBarTabContainerContext } from "../context/TabBarTabContainerContext"
import { useTabBarBaseTabViewModel } from "../model/useTabBarBaseTabViewModel"
import type {
  TabBarBaseTabDnDOverrides,
  TabBarTabDivProps
} from "./TabBarBaseTab"
import { TabBarTabButton } from "./TabBarBaseTab"
import type { TabBarTabProps } from "./TabBarTab"
import { TabBarTabTooltipContent } from "./TabBarTabTooltipContent"

type TabBarSimpleTabProps = TabBarTabProps & TabBarBaseTabDnDOverrides

export const TabBarSimpleTab = React.memo(
  React.forwardRef<HTMLDivElement, TabBarSimpleTabProps>(
    (
      {
        eventKey,
        index,
        label,
        icon,
        count,
        truncationThreshold,
        className,
        disabled,
        variant,
        "aria-controls": ariaControls,
        "aria-label": ariaLabel,
        dndRef,
        dndListeners,
        isDragging = false,
        insertBefore = false,
        insertAfter = false,
        isSortable = false
      },
      ref
    ) => {
      const {
        selected,
        displayLabel,
        contextValue,
        tabRef,
        tabId,
        onTabSelect
      } = useTabBarBaseTabViewModel({
        eventKey,
        index,
        label,
        count,
        truncationThreshold,
        isDragging,
        insertBefore,
        insertAfter
      })

      const mergedRefs = useMergedRefs(tabRef, ref, dndRef)

      const handleClick = useEvent(() => {
        if (!disabled) onTabSelect(eventKey)
      })

      const handleKeyDown = useEvent(
        (e: React.KeyboardEvent<HTMLDivElement>) => {
          if (e.key === "Enter" || e.key === " ") {
            if (e.target !== e.currentTarget) return
            e.preventDefault()
            onTabSelect(eventKey)
          }
        }
      )

      const tabButtonProps: TabBarTabDivProps = {
        tabId,
        selected,
        ariaControls,
        ariaLabel,
        disabled,
        hasActions: false,
        menuOpen: false,
        className,
        isSortable,
        variant,
        index,
        isDragging,
        insertBefore,
        insertAfter,
        icon,
        count,
        displayLabel,
        dndListeners,
        onClick: handleClick,
        onKeyDown: handleKeyDown
      }

      return (
        <TabBarTabContainerContext.Provider value={contextValue}>
          <Tooltip
            content={<TabBarTabTooltipContent text={label} />}
            placement="top"
          >
            <TabBarTabButton ref={mergedRefs} {...tabButtonProps} />
          </Tooltip>
        </TabBarTabContainerContext.Provider>
      )
    }
  )
)

TabBarSimpleTab.displayName = "TabBar.SimpleTab"
